import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Label } from '../../components/ui/Label';
import {
  Search, Filter, Eye, Clock, Tag,
  BookOpen, FileText, Video, Image, Download, Flag,
  TrendingUp, TrendingDown, BarChart3, PieChart, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Info, HelpCircle, Lightbulb, Zap, Target, Award,
  Calendar, MapPin, Phone, Mail, MessageSquare, Send, Plus, Minus,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, MoreHorizontal,
  MoreVertical, Copy, ExternalLink, Lock, Unlock, Shield, Database,
  Server, Globe, Network, Wifi, Signal, Battery, Volume, Volume2,
  VolumeX, Play, Pause, Stop, SkipBack, SkipForward, RotateCcw,
  RotateCw, Maximize, Minimize, Maximize2, Minimize2, Square, Circle,
  Triangle, Hexagon, Octagon, Pentagon, Diamond,
  PieChart as PieChartIcon, ArrowUp, ArrowDown, ArrowLeft, ArrowRight
} from 'lucide-react';
import { articleService, extractRegistrationListData } from '../../api/articleService';

/** Map registration API article rows to UI cards (search returns active articles only server-side). */
function mapKnowledgeArticleToResult(a) {
  const helpful = a.helpfulCount ?? 0;
  const notHelpful = a.notHelpfulCount ?? 0;
  const votes = helpful + notHelpful;
  const rating = votes > 0 ? Math.round((helpful / votes) * 50) / 10 : null;
  const rawCat = a.category || 'General';
  return {
    id: a.articleId ?? a.id,
    title: a.title,
    question: a.question || '',
    excerpt: (a.question || '').slice(0, 280) || (a.answer || '').slice(0, 280),
    content: a.answer || '',
    category: rawCat,
    categoryKey: rawCat.toLowerCase().replace(/\s+/g, '_'),
    type: 'article',
    rating: rating ?? 0,
    ratingCount: votes,
    views: a.usedCount ?? 0,
    author: a.createdBy || 'Support',
    tags: Array.isArray(a.tags) ? a.tags : [],
    updatedAt: a.updatedAt || a.createdAt,
    userRating: null,
    bookmarked: false
  };
}

const KnowledgeSearch = ({
  initialQuery = '',
  runSearchOnMount = false,
  /** Landing / unsigned: use no-auth search API (same active/published rules). */
  usePublicSearch = false,
  /** When usePublicSearch, optional tenant scope (org from domain or session). */
  publicSearchTenantId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [filters, setFilters] = useState({
    category: 'all',
    type: 'all',
    rating: 'all',
    dateRange: 'all',
    language: 'all'
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [activeTab, setActiveTab] = useState('search'); // 'search', 'history', 'analytics'
  const [selectedResult, setSelectedResult] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    const loadSearchHistory = () => {
      const history = JSON.parse(localStorage.getItem('knowledgeSearchHistory') || '[]');
      setSearchHistory(history);
    };
    loadSearchHistory();
  }, []);

  const runKnowledgeSearch = useCallback(async (query) => {
    if (!query.trim()) return;

    setLoading(true);
    setError(null);

    try {
      let response;
      if (usePublicSearch) {
        const tid =
          publicSearchTenantId != null && publicSearchTenantId !== ''
            ? Number(publicSearchTenantId)
            : undefined;
        const opts = Number.isFinite(tid) ? { tenantId: tid } : {};
        response = await articleService.searchArticlesNoAuth(query.trim(), opts);
      } else {
        // Authenticated: customers only get published/live rows (includeDrafts false).
        response = await articleService.searchArticles(query.trim(), {
          includeDrafts: false
        });
      }

      let rawList = extractRegistrationListData(response);
      rawList = rawList.filter((a) => {
        const s = (a.status || '').toLowerCase();
        return s === 'active' || s === 'published';
      });

      let mapped = rawList.map(mapKnowledgeArticleToResult);

      if (filters.category && filters.category !== 'all') {
        mapped = mapped.filter((r) => (r.categoryKey || '') === filters.category);
      }
      if (filters.type && filters.type !== 'all') {
        mapped = mapped.filter((r) => r.type === filters.type);
      }

      setSearchResults(mapped);

      setSearchHistory((prev) => {
        const newHistory = [
          { query, timestamp: new Date().toISOString(), resultsCount: mapped.length },
          ...prev.filter((item) => item.query !== query).slice(0, 9)
        ];
        localStorage.setItem('knowledgeSearchHistory', JSON.stringify(newHistory));
        return newHistory;
      });
    } catch (err) {
      setError('Error performing search');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, usePublicSearch, publicSearchTenantId]);

  const handleSearch = useCallback(
    async (queryOverride) => {
      const q = typeof queryOverride === 'string' ? queryOverride : searchQuery;
      await runKnowledgeSearch(q);
    },
    [searchQuery, runKnowledgeSearch]
  );

  const didRunInitialFromDashboard = useRef(false);
  useEffect(() => {
    if (!runSearchOnMount || !initialQuery?.trim()) return;
    if (didRunInitialFromDashboard.current) return;
    didRunInitialFromDashboard.current = true;
    const q = initialQuery.trim();
    setSearchQuery(q);
    void runKnowledgeSearch(q);
  }, [runSearchOnMount, initialQuery, runKnowledgeSearch]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleSortChange = (sortType) => {
    setSortBy(sortType);
    if (searchResults.length > 0) {
      handleSearch();
    }
  };

  /* Future: like/dislike/bookmark via API
  const handleRateArticle = async (articleId, rating) => { ... };
  const handleBookmarkArticle = async (articleId) => { ... };
  */

  const getSearchSuggestions = () => {
    const commonSearches = [
      'How to reset password',
      'Software installation guide',
      'Network troubleshooting',
      'Email configuration',
      'System requirements',
      'Backup procedures',
      'Security policies',
      'User permissions'
    ];
    
    return commonSearches.filter(suggestion => 
      suggestion.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const getResultIcon = (type) => {
    switch (type) {
      case 'article': return <FileText className="h-5 w-5" />;
      case 'video': return <Video className="h-5 w-5" />;
      case 'image': return <Image className="h-5 w-5" />;
      case 'document': return <BookOpen className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  /* Future: metadata row (views, ratings, date, author)
  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };
  */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Advanced Knowledge Search</h1>
          <p className="text-muted-foreground mt-2">
            Search and discover knowledge articles with advanced filtering and analytics
          </p>
        </div>
        {/* <Button
          variant="outline"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        >
          <Filter className="h-4 w-4 mr-2" />
          Advanced Filters
        </Button> */}
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'search', label: 'Search', icon: Search },
            { id: 'history', label: 'History', icon: Clock },
            // { id: 'analytics', label: 'Analytics', icon: BarChart3 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* Search Bar */}
          <Card>
            <CardContent className="p-6">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search knowledge base..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Search Suggestions */}
                  {searchQuery && getSearchSuggestions().length > 0 && (
                    <div className="mt-2 p-2 bg-muted rounded-lg">
                      <div className="text-sm text-muted-foreground mb-2">Suggestions:</div>
                      <div className="flex flex-wrap gap-2">
                        {getSearchSuggestions().slice(0, 5).map((suggestion, index) => (
                          <Button
                            key={index}
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSearchQuery(suggestion);
                              handleSearch(suggestion);
                            }}
                            className="text-xs"
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <Button onClick={() => handleSearch()} disabled={loading}>
                  {loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Advanced Filters
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={filters.category} onValueChange={(value) => handleFilterChange('category', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="user_guide">User Guide</SelectItem>
                        <SelectItem value="troubleshooting">Troubleshooting</SelectItem>
                        <SelectItem value="policy">Policy</SelectItem>
                        <SelectItem value="faq">FAQ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="type">Content Type</Label>
                    <Select value={filters.type} onValueChange={(value) => handleFilterChange('type', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="article">Article</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="image">Image</SelectItem>
                        <SelectItem value="document">Document</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="rating">Rating</Label>
                    <Select value={filters.rating} onValueChange={(value) => handleFilterChange('rating', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Ratings" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Ratings</SelectItem>
                        <SelectItem value="5">5 Stars</SelectItem>
                        <SelectItem value="4">4+ Stars</SelectItem>
                        <SelectItem value="3">3+ Stars</SelectItem>
                        <SelectItem value="2">2+ Stars</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="dateRange">Date Range</Label>
                    <Select value={filters.dateRange} onValueChange={(value) => handleFilterChange('dateRange', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="week">This Week</SelectItem>
                        <SelectItem value="month">This Month</SelectItem>
                        <SelectItem value="year">This Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="language">Language</Label>
                    <Select value={filters.language} onValueChange={(value) => handleFilterChange('language', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Languages" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Languages</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <div>
                    <Label htmlFor="sortBy">Sort By</Label>
                    <Select value={sortBy} onValueChange={handleSortChange}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="relevance">Relevance</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="rating">Rating</SelectItem>
                        <SelectItem value="views">Views</SelectItem>
                        <SelectItem value="title">Title</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={() => handleSearch()} disabled={loading}>
                    Apply Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search Results */}
          {error && (
            <Card className="border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  {error}
                </div>
              </CardContent>
            </Card>
          )}

          {searchResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">
                  Search Results ({searchResults.length})
                </h3>
                <div className="text-sm text-muted-foreground">
                  Found in {Math.random() * 0.5 + 0.1}s
                </div>
              </div>

              {searchResults.map((result) => (
                <Card key={result.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                          {getResultIcon(result.type)}
                          <h4 className="text-lg font-semibold text-foreground">{result.title}</h4>
                          <Badge variant="outline">{result.category}</Badge>
                          <Badge variant="secondary">{result.type}</Badge>
                        </div>

                        <p className="text-muted-foreground mb-3 line-clamp-3">{result.excerpt}</p>

                        {/* Future: views, ratings, updated date, author
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {result.views} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Star className={`h-4 w-4 ${getRatingColor(result.rating)}`} />
                            {result.rating}/5 ({result.ratingCount} ratings)
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(result.updatedAt).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {result.author}
                          </span>
                        </div>
                        */}

                        {result.tags && result.tags.length > 0 && (
                          <div className="flex items-center gap-2 mt-3">
                            <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="flex flex-wrap gap-1">
                              {result.tags.map((tag, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setSelectedResult(result)}
                        aria-label="View full article"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {searchQuery && searchResults.length === 0 && !loading && (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No results found</h3>
                  <p>Try adjusting your search terms or filters</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Search History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {searchHistory.length > 0 ? (
                <div className="space-y-3">
                  {searchHistory.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 border rounded-lg">
                      <div>
                        <div className="font-medium">{item.query}</div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(item.timestamp).toLocaleString()} • {item.resultsCount} results
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSearchQuery(item.query);
                          setActiveTab('search');
                          handleSearch(item.query);
                        }}
                      >
                        Search Again
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No search history yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Search Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">1,247</div>
                  <div className="text-sm text-blue-600">Total Searches</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">89%</div>
                  <div className="text-sm text-green-600">Success Rate</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">2.3s</div>
                  <div className="text-sm text-yellow-600">Avg Response Time</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">156</div>
                  <div className="text-sm text-purple-600">Unique Articles</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Popular Searches</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { query: 'Password reset', count: 45 },
                        { query: 'Email setup', count: 32 },
                        { query: 'Software installation', count: 28 },
                        { query: 'Network issues', count: 24 },
                        { query: 'Backup procedures', count: 19 }
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span>{item.query}</span>
                          <Badge variant="secondary">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Categories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { category: 'Technical', count: 89 },
                        { category: 'User Guide', count: 67 },
                        { category: 'Troubleshooting', count: 54 },
                        { category: 'Policy', count: 32 },
                        { category: 'FAQ', count: 28 }
                      ].map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span>{item.category}</span>
                          <Badge variant="secondary">{item.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Modal
        isOpen={!!selectedResult}
        onClose={() => setSelectedResult(null)}
        title={selectedResult?.title || 'Article'}
        size="lg"
      >
        {selectedResult && (
          <div className="space-y-5 text-left pb-2">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{selectedResult.category}</Badge>
              <Badge variant="secondary">{selectedResult.type}</Badge>
            </div>

            {selectedResult.question ? (
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Question</h4>
                <p className="text-foreground whitespace-pre-wrap leading-relaxed">{selectedResult.question}</p>
              </div>
            ) : null}

            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">Answer</h4>
              <div className="text-foreground whitespace-pre-wrap leading-relaxed">
                {selectedResult.content || '—'}
              </div>
            </div>

            {selectedResult.tags && selectedResult.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Tags</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedResult.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KnowledgeSearch;
